import { SemiAutoCompleteOption } from '@components/TextBox';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { requiredStringField, stringField } from '@utils/forms';
import { formatSlashDate } from '@utils/types';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export const baseVesselScheduleItem = t.type({
  vessel: t.string,
  callsign: t.string,
  voyage: t.string,
  pol: h.omittable(t.string),
  pod: h.omittable(t.string),
  etd: h.omittable(t.string),
  eta: h.omittable(t.string),
  atd: h.omittable(t.string),
  ata: h.omittable(t.string),
});

export type BaseVesselScheduleItem = t.TypeOf<typeof baseVesselScheduleItem>;

export const vesselScheduleItem = t.type({
  id: t.number,
  ...baseVesselScheduleItem.props,
});

export type VesselScheduleItem = t.TypeOf<typeof vesselScheduleItem>;

export const vesselScheduleItemCreate = t.type({
  vessel: t.string,
  callsign: t.string,
  voyage: t.string,
  pol: t.string,
  pod: t.string,
  etd: h.omittable(t.string),
  eta: h.omittable(t.string),
  atd: h.omittable(t.string),
  ata: h.omittable(t.string),
});

export type VesselScheduleItemCreate = t.TypeOf<typeof vesselScheduleItemCreate>;

export const vesselScheduleItemPatch = t.type({
  vessel: t.string,
  callsign: t.string,
  voyage: t.string,
  pol: t.string,
  pod: t.string,
  etd: h.omittable(t.string),
  eta: h.omittable(t.string),
});

export type VesselScheduleItemPatch = VesselScheduleItemCreate;

export const vesselScheduleItemForm = t.type({
  vessel: requiredStringField(vesselScheduleStrings.vessel),
  callsign: requiredStringField(vesselScheduleStrings.callsign),
  voyage: requiredStringField(vesselScheduleStrings.voyage),
  pol: stringField(vesselScheduleStrings.pol),
  pod: stringField(vesselScheduleStrings.pod),
  etd: stringField(vesselScheduleStrings.etd),
  eta: stringField(vesselScheduleStrings.eta),
  atd: stringField(vesselScheduleStrings.atd),
  ata: stringField(vesselScheduleStrings.ata),
});

export type VesselScheduleItemForm = t.TypeOf<typeof vesselScheduleItemForm>;

export const mapVesselScheduleItemFormToVesselScheduleItemCreate = (
  v: VesselScheduleItemForm,
): VesselScheduleItemCreate => {
  return {
    ...v,
    pol: v.pol,
    pod: v.pod,
    etd: (v.etd && formatSlashDate(v.etd)) || null,
    eta: (v.eta && formatSlashDate(v.eta)) || null,
    atd: (v.atd && formatSlashDate(v.atd)) || null,
    ata: (v.ata && formatSlashDate(v.ata)) || null,
  };
};

export const mapVesselScheduleItemFormToVesselScheduleItemPatch =
  mapVesselScheduleItemFormToVesselScheduleItemCreate;

export const mapVesselScheduleItemToVesselScheduleItemForm = (
  v: VesselScheduleItem,
): VesselScheduleItemForm => {
  return {
    vessel: v.vessel,
    callsign: v.callsign,
    voyage: v.voyage,
    pol: v.pol ?? '',
    pod: v.pod ?? '',
    etd: v.etd ?? '',
    eta: v.eta ?? '',
    atd: v.atd ?? '',
    ata: v.ata ?? '',
  };
};

export const vesselScheduleItemFormInitialValues: VesselScheduleItemForm = {
  vessel: '',
  callsign: '',
  voyage: '',
  pol: '',
  pod: '',
  etd: '',
  eta: '',
  atd: '',
  ata: '',
};

export const addVesselScheduleItemsForm = t.type({
  items: h.array(vesselScheduleItemForm).min(1, vesselScheduleStrings.vesselScheduleItemsRequired),
});

export type AddVesselScheduleItemsForm = t.TypeOf<typeof addVesselScheduleItemsForm>;

export const vesselScheduleQueryWithoutPage = t.type({
  vessel: h.omittable(t.string),
  voyage: h.omittable(t.string),
  pol: h.omittable(t.string),
  pod: h.omittable(t.string),
  etd_after: h.omittable(t.string),
  eta_after: h.omittable(t.string),
  etd_before: h.omittable(t.string),
  eta_before: h.omittable(t.string),
  has_atd: h.omittable(t.boolean),
  ordering: h.omittable(t.string),
});

export type VesselScheduleQueryWithoutPage = t.TypeOf<typeof vesselScheduleQueryWithoutPage>;

export const vesselScheduleQueryWithoutPageInitialValues: VesselScheduleQueryWithoutPage = {
  vessel: '',
  voyage: '',
  pol: '',
  pod: '',
  etd_after: '',
  eta_after: '',
  etd_before: '',
  eta_before: '',
  has_atd: undefined,
  ordering: undefined,
};

export const vesselScheduleQuery = t.partial({
  ...vesselScheduleQueryWithoutPage.props,
  page: h.omittable(h.number().cast()),
  page_size: h.omittable(h.number().cast()),
});

export type VesselScheduleQuery = t.TypeOf<typeof vesselScheduleQuery>;

export interface VesselSemiAutoCompleteOption extends SemiAutoCompleteOption {
  value: {
    vessel: string;
    callsign: string;
  };
}
